import CallIcon from '@mui/icons-material/Call';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import { Alert, AlertTitle, AppBar, Box, CircularProgress, Toolbar, Typography } from '@mui/material';
import { ShareLinkError } from '~/api/share';
import reportCover from '~static/bg-solar.png';
import logoBlueSrc from '~static/logo-blue.png';
import { ReportContainer, SectionContainer } from './style';

const DEFAULT_TITLE = 'This shared report is unavailable';
const DEFAULT_MESSAGE =
  'The link may have expired, or you do not have permission to view it. Please contact the person who shared this report with you.';

const ShareReportHeader: React.FC = () => (
  <AppBar component="nav" position="static" sx={{ bgcolor: 'common.black' }}>
    <Toolbar>
      <SectionContainer sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ flexGrow: 1 }}>
          <Box component="img" src={logoBlueSrc} sx={{ height: 100 }} />
        </Box>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', pb: 1 }}>
            <Typography
              component="a"
              variant="h6"
              color="brand.main"
              href="tel:1300118834"
              sx={{ display: 'flex', alignItems: 'center', pl: 2 }}
            >
              <CallIcon /> &nbsp;1300 11 88 34
            </Typography>
            <Typography
              component="a"
              variant="h6"
              color="brand.main"
              href="mailto:1300118834"
              sx={{ display: 'flex', alignItems: 'center', pl: 2 }}
            >
              <EmailIcon /> &nbsp;info@tteg.com.au
            </Typography>
            <Typography
              component="a"
              variant="h6"
              color="brand.main"
              href="https://tteg.com.au"
              target="_blank"
              sx={{ display: 'flex', alignItems: 'center', pl: 2 }}
            >
              <LanguageIcon /> &nbsp;tteg.com.au
            </Typography>
          </Box>
        </Box>
      </SectionContainer>
    </Toolbar>
  </AppBar>
);

const ShareReportLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ReportContainer sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
    <ShareReportHeader />
    <Box
      component="main"
      sx={{
        display: 'flex',
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        backgroundImage: `url(${reportCover})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundAttachment: 'fixed'
      }}
    >
      {children}
    </Box>
  </ReportContainer>
);

interface ShareReportErrorProps {
  error: unknown;
}

export const ShareReportError: React.FC<ShareReportErrorProps> = ({ error }) => {
  const isShareError = error instanceof ShareLinkError;
  const title = (isShareError && error.title) || DEFAULT_TITLE;
  const message = isShareError && error.message ? error.message : DEFAULT_MESSAGE;

  return (
    <ShareReportLayout>
      <Alert
        severity="warning"
        variant="outlined"
        sx={{
          maxWidth: 600,
          width: '100%',
          alignItems: 'flex-start',
          bgcolor: 'rgba(255, 255, 255, 0.92)',
          backdropFilter: 'blur(4px)',
          boxShadow: 3,
          color: 'text.primary',
          '& .MuiAlert-icon': { color: 'warning.main', padding: 0, marginTop: '13px' }
        }}
      >
        <AlertTitle sx={{ m: 0, mb: 1, fontSize: 24, fontWeight: 600, lineHeight: '32px' }}>{title}</AlertTitle>
        <Typography sx={{ fontSize: 18 }}>{message}</Typography>
      </Alert>
    </ShareReportLayout>
  );
};

export const ShareReportLoading: React.FC = () => (
  <ShareReportLayout>
    <CircularProgress size={56} sx={{ color: 'brand.main' }} />
  </ShareReportLayout>
);

export default ShareReportError;
